import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { MapContext } from "../context/MapContext";
import {
  Func_chk_plateau_dem,
  Func_chk_radio_plateau_building_2d,
  Func_chk_radio_plateau_building_3d,
  Func_chk_radio_plateau_building_nodisp,
  downloadImageFromBase64,
} from "../utils/Util";
import PromotionalArea from "./PromotionalArea";
import {
  BASE_DPI,
  F_Hude_fill_opacity,
  StyleURL,
  StyleURL_Gray,
  MAPPLE_COM_LINKS,
  NINI_BETA_NOTICE,
  ABOUT_SECTION_NINI
} from "../constants/Constant";
import PdfModal from "./modal/PdfModal";
import { useAuthStatus } from "../context/AuthContext";
import { Button, Form, Spinner, ToggleButton } from "react-bootstrap";
import { loadState, resetState, saveState } from "../utils/SaveStateUtil";
import { applyNiniFilter, transformStyleForTD } from "../utils/MapManager";
import MapSelectionOverlay from "./MapSelectionOverlay";
import { toElement } from "../lib/maplibre-gl-map-to-image";

const LeftSide = () => {
  const { map, hasUrlHash, isCheckSaveState, setIsCheckSaveState } =
    useContext(MapContext);
  const { userInfo, isLoading, isUserLoggedIn } = useAuthStatus();
  const [isCheckImageDownload, setIsCheckImageDownload] = useState(false);
  const imageRef = useRef(null);
  const [isCreatingImage, setIsCreatingImage] = useState(false);
  const [isCheckGray, setIsCheckGray] = useState(false);
  const [isCheckPolygon, setIsCheckPolygon] = useState(true);
  const [isCheckPhoto, setIsCheckPhoto] = useState(true);
  // 任意座標の表示（保存値があればそれを優先し、なければ表示ON）
  const [isCheckNini, setIsCheckNini] = useState(
    () => loadState("checkMapState")?.isCheckNini ?? true
  );
  const [radioSelect, setRadioSelect] = useState("nodisp");
  const [isCheckDem, setIsCheckDem] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const isEnabledSaveStateRef = useRef(false);
  const isFirstLoadRef = useRef(true);
  const [isSideOpen, setIsSideOpen] = useState(false);

  // TODO:サブスクリプションフラグの確定
  const isEnabledSubscription =
    userInfo?.attributes &&
      userInfo.attributes["custom:subscription_status"] === "active"
      ? true
      : false;

  const createImage = useCallback(
    async (selectionData) => {
      // スピナー表示
      setIsCreatingImage(true);

      try {
        // 画像作成
        await new Promise((resolve) => setTimeout(resolve, 200)); // 遅延させてUIを更新する
        await toElement(map, {
          targetImageId: "mapImage",
          coverEdits: true,
          format: "png",
          hideControlsInCorner: ["top-right"],
          hideMarkers: true,
          size: [selectionData.sizeCssPx.height, selectionData.sizeCssPx.width],
          dpi: BASE_DPI * (window.devicePixelRatio || 1),
          center: selectionData.selectionCenter, // 選択範囲の中心を渡す
        });

        // ダウンロード
        const img = imageRef.current;
        if (img && img.src) {
          downloadImageFromBase64(img.src, "map_image.png");
          img.src = null;
        }
      } finally {
        // スピナー表示終了
        setIsCreatingImage(false);
      }
    },
    [map, imageRef]
  );

  // 地図の位置を保存
  const saveLocation = useCallback(() => {
    if (isEnabledSaveStateRef.current) {
      saveState("location", {
        center: map.getCenter(), // 中心（緯度経度）
        zoom: map.getZoom(), // ズームレベル
        bearing: map.getBearing(), // 回転
        pitch: map.getPitch(), // 傾き
      });
    }
  }, [map]);
  useEffect(() => {
    if (!map) {
      return;
    }
    map.on("moveend", saveLocation);
  }, [map, saveLocation]);

  useEffect(() => {
    if (isLoading || !map) {
      return;
    }
    // 初期表示時の状態読込
    if (isFirstLoadRef.current) {
      if (isUserLoggedIn) {
        const location = loadState("location");
        const checkMapState = loadState("checkMapState");
        if (location && !hasUrlHash) {
          map.setCenter(location.center);
          map.setZoom(location.zoom);
          map.setBearing(location.bearing);
          map.setPitch(location.pitch);
        }
        if (checkMapState) {
          setIsCheckGray(checkMapState.isCheckGray);
          setIsCheckPolygon(checkMapState.isCheckPolygon);
          setIsCheckPhoto(checkMapState.isCheckPhoto);
          setIsCheckNini(checkMapState.isCheckNini ?? true);

          // 地図に反映(ポリゴンの塗り、空中写真、任意座標の設定)
          if (map.isStyleLoaded()) {
            setPolygonStatus(checkMapState.isCheckPolygon);
            setPhotoStatus(checkMapState.isCheckPhoto);
            setNiniStatus(checkMapState.isCheckNini ?? true);
          } else {
            map.once("load", () => {
              setPolygonStatus(checkMapState.isCheckPolygon);
              setPhotoStatus(checkMapState.isCheckPhoto);
              setNiniStatus(checkMapState.isCheckNini ?? true);
            });
          }
        }
      }
      isFirstLoadRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isLoading, isUserLoggedIn, hasUrlHash]);

  useEffect(() => {
    if (isLoading || isFirstLoadRef.current) {
      // ログイン状態や初期読込が未完了
      return;
    }
    // 保存可否を設定
    if (isUserLoggedIn) {
      isEnabledSaveStateRef.current = isCheckSaveState;
    } else {
      // ログオフした場合は、再現スイッチもOFFにする
      isEnabledSaveStateRef.current = false;
      setIsCheckSaveState(false);
    }

    if (!isEnabledSaveStateRef.current) {
      // 未ログイン、または再現OFF時は削除
      resetState();
    } else if (isCheckSaveState) {
      // チェックを入れたときに一度保存
      saveLocation();
    }
  }, [
    isLoading,
    isUserLoggedIn,
    isCheckSaveState,
    setIsCheckSaveState,
    saveLocation,
  ]);

  // 次回終了状態再現、地図の表示設定の状態保存
  useEffect(() => {
    if (isEnabledSaveStateRef.current) {
      saveState("checkMapState", {
        isCheckSaveState,
        isCheckGray,
        isCheckPolygon,
        isCheckPhoto,
        isCheckNini,
      });
    }
  }, [isCheckSaveState, isCheckGray, isCheckPolygon, isCheckPhoto, isCheckNini]);

  // ログイン状態が変化したら任意座標の表示を再適用
  useEffect(() => {
    if (!map) {
      return;
    }
    applyNiniFilter(map, { isUserLoggedIn });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, isUserLoggedIn]);

  // 図形機能の利用可否制御
  useEffect(() => {
    // ボタンの状態を更新する関数
    const updateButtonState = (btn) => {
      if (isEnabledSubscription) {
        btn.disabled = false;
        btn.title = btn.parentNode.firstChild.classList.contains(
          "maplibregl-terradraw-measure-add-control"
        )
          ? "図形切替"
          : "計測切替";
      } else {
        btn.disabled = true;
        btn.title = btn.parentNode.firstChild.classList.contains(
          "maplibregl-terradraw-measure-add-control"
        )
          ? "図形機能切替：この機能はサブスクリプションの申し込みが必要です。"
          : "ログインしてください";
      }
    };

    // すでに要素があるか確認
    const existingBtn = document.querySelector("#terradrawSwitchBtn");
    if (existingBtn) {
      updateButtonState(existingBtn);
      return;
    }

    // 要素がない場合は、DOMの変更を監視する（MutationObserver）
    const observer = new MutationObserver((mutations, obs) => {
      const targetBtn = document.querySelector("#terradrawSwitchBtn");
      if (targetBtn) {
        updateButtonState(targetBtn);
        obs.disconnect(); // 見つかったら監視を終了
      }
    });

    // body全体のDOM変化を監視
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // クリーンアップ関数（コンポーネントがアンマウントされたら監視を止める）
    return () => observer.disconnect();

  }, [isEnabledSubscription]);

  // isCheckImageDownload の状態に応じて範囲選択モードを切り替える
  useEffect(() => {
    // mapオブジェクトがまだなければ何もしない
    if (!map) {
      return;
    }

    if (isCheckImageDownload) {
      // サイドバーを閉じて地図を広くする
      if (isSideOpen) {
        setIsSideOpen(false);
      }
      if (document.querySelector(".r-side")?.classList.contains("active")) {
        window.dispatchEvent(new CustomEvent("toggleRightSide"));
      }
    }
  }, [map, isCheckImageDownload, isSideOpen]);

  // 背景地図をグレー表示
  function checkboxStatusGray(
    grayChecked,
    polygonChecked = isCheckPolygon,
    photoChecked = isCheckPhoto
  ) {
    setIsCheckGray(grayChecked);
    // スタイル変更
    map.setStyle(grayChecked ? StyleURL_Gray : StyleURL, {
      diff: false,
      transformStyle: transformStyleForTD,
    });
    // スタイル変更後に他のチェック状態を反映
    map.once("styledata", () => {
      setPolygonStatus(polygonChecked);
      setPhotoStatus(photoChecked);
    });
  }
  // 地番ポリゴンの塗りを表示
  const setPolygonStatus = (checked) => {
    if (map.getLayer("hude")) {
      const opacity = checked ? F_Hude_fill_opacity : 0;
      map.setPaintProperty("hude", "fill-opacity", [
        "step",
        ["zoom"],
        opacity,
        12,
        ["case", ["boolean", ["feature-state", "hover"], false], 0.5, opacity],
      ]);
    }
    // 任意座標の塗りにも適用（塗りOFFでもマウスオーバー中の筆のみ塗る）
    if (map.getLayer("nini_kukaku_fill")) {
      const nini_opacity = checked ? F_Hude_fill_opacity : 0;
      map.setPaintProperty("nini_kukaku_fill", "fill-opacity", [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        0.5,
        nini_opacity,
      ]);
    }
  };
  // 拡大時に空中写真を表示
  const setPhotoStatus = (checked) => {
    map.setLayoutProperty(
      "gsiphoto",
      "visibility",
      checked ? "visible" : "none"
    );
  };
  // 任意座標の図郭を表示
  const setNiniStatus = (checked) => {
    if (!map) {
      return;
    }
    applyNiniFilter(map, { isNiniOn: checked, isUserLoggedIn });
  };
  function OnLinkClick() {
    window.dispatchEvent(new CustomEvent("showAboutViewerModal"));
  }
  // 任意座標の注意書き（「MAPPLE法務局地図ビューア」についてモーダルの該当の章）を開く
  const openNiniNoticeDetail = () => {
    window.dispatchEvent(
      new CustomEvent("showAboutViewerModal", {
        detail: { section: ABOUT_SECTION_NINI },
      })
    );
  };
  // トグルのラベル内に置いたアイコン用。既定動作を止めてトグルの反転を防ぐ
  const onNiniNoticeIconClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    openNiniNoticeDetail();
  };
  const onNiniNoticeIconKeyDown = (e) => {
    if (e.key !== "Enter" && e.key !== " ") {
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    openNiniNoticeDetail();
  };
  const pStyle = {
    display: "block",
    marginBlockStart: "1em",
    marginBlockEnd: "1em",
    marginInlineStart: "0px",
    marginInlineEnd: "0px",
    unicodeBidi: "isolate",
    marginTop: 0,
    marginBottom: "1rem",
  };

  return (
    <div className={`l-side ${isSideOpen ? "active" : ""}`}>
      <div
        className={`l-side-open ${isSideOpen ? "active" : ""}`}
        id="gsi--side--open"
        onClick={() => setIsSideOpen(!isSideOpen)}
      ></div>
      <div className="l-side__inner">

        {/* --- ビューア情報 --- */}
        <div style={pStyle}></div>
        <nav id="aboutThisViewer">
          <a href="#_" onClick={OnLinkClick}>
            MAPPLE法務局地図ビューアについて、使い方
          </a>
        </nav>

        {/* --- サブスクリプション機能 --- */}
        <div style={pStyle}></div>
        <div className="mx-2 mb-3">
          <legend className="col-form-label bg-light mb-2" style={{ display: 'flex', alignItems: 'center', padding: '0.25rem 0.5rem' }}>
            サブスクリプション
          </legend>
          <div className="d-flex flex-wrap mt-2">
            <span
              className="d-block w-100 mb-2"
              title={!isEnabledSubscription ? "この機能はサブスクリプションの申し込みが必要です。" : ""}
            >
              <Button
                className="w-100"
                disabled={!isEnabledSubscription}
                onClick={() => { 
                  if (isCheckDem) {
                    alert("3D都市モデルの地形の表示中は、地図印刷はできません。3D地形をOFFにしてください");
                    return;
                  }
                  setShowPdfModal(true); 
                }}
              >
                地図印刷（PDFダウンロード）
              </Button>
            </span>
            <span
              className="d-block w-100 mb-2"
              title={!isEnabledSubscription ? "この機能はサブスクリプションの申し込みが必要です。" : ""}
            >
              <ToggleButton
                id="chkImageDownload"
                type="checkbox"
                className="w-100"
                variant="primary"
                checked={isCheckImageDownload}
                onChange={(e) => {
                  const isChecked = e.currentTarget.checked;
                  if (isChecked && isCheckDem) {
                    alert("3D都市モデルの地形の表示中は、画像作成はできません。3D地形をOFFにしてください");
                    return;
                  }
                  setIsCheckImageDownload(isChecked)
                }}
                disabled={!isEnabledSubscription}
              >
                {isCheckImageDownload ? "画像作成キャンセル" : "画像作成"}
              </ToggleButton>
            </span>
          </div>
          <PdfModal
            show={showPdfModal}
            handleClose={() => { setShowPdfModal(false); }}
            map={map}
            isCheckSaveState={isCheckSaveState}
          />
        </div>

        {/* --- 地図画像（非表示） --- */}
        <div className="w-100 px-2">
          <img
            ref={imageRef}
            id="mapImage"
            alt=""
            style={{ maxWidth: "100%", display: "none" }}
          />
        </div>

        {/* --- 地図表示設定 --- */}
        <div style={pStyle}></div>
        <div className="mx-2 mb-3">
          <legend className="col-form-label bg-light mb-2" style={{ display: 'flex', alignItems: 'center', padding: '0.25rem 0.5rem' }}>
            地図表示設定
          </legend>
          <div className="mt-2">
            <Form.Switch
              id="chkSaveState"
              className="mb-2"
              label="次回終了状態を再現"
              disabled={!isUserLoggedIn}
              checked={isCheckSaveState}
              onChange={(e) => { setIsCheckSaveState(e.target.checked); }}
            />
            <Form.Switch
              id="chk_gray"
              className="mb-2"
              label="背景地図をグレー表示"
              checked={isCheckGray}
              onChange={(e) => checkboxStatusGray(e.target.checked)}
            />
            <Form.Switch
              id="chk_polygon"
              className="mb-2"
              label="地番ポリゴンの塗りを表示"
              checked={isCheckPolygon}
              onChange={(e) => {
                setIsCheckPolygon(e.target.checked);
                setPolygonStatus(e.target.checked);
              }}
            />
            <Form.Switch
              id="chk_photo"
              className="mb-2"
              label="拡大時に空中写真を表示"
              checked={isCheckPhoto}
              onChange={(e) => {
                setIsCheckPhoto(e.target.checked);
                setPhotoStatus(e.target.checked);
              }}
            />
            <Form.Switch
              id="chk_nini"
              className="mb-2"
              label={
                <>
                  位置合わせした任意座標の表示（β版）
                  {/* ラベル文字列に続けて表示する（クリックはトグルに伝えない） */}
                  <span
                    role="button"
                    tabIndex={0}
                    className="ms-1 text-primary"
                    style={{ whiteSpace: "nowrap" }}
                    title={NINI_BETA_NOTICE.HINT}
                    aria-label={NINI_BETA_NOTICE.TITLE}
                    onClick={onNiniNoticeIconClick}
                    onKeyDown={onNiniNoticeIconKeyDown}
                  >
                    <i
                      className="fa fa-info-circle"
                      aria-hidden="true"
                      style={{ fontSize: "1.1rem" }}
                    ></i>
                  </span>
                </>
              }
              checked={isCheckNini}
              onChange={(e) => {
                setIsCheckNini(e.target.checked);
                setNiniStatus(e.target.checked);
              }}
            />
          </div>
        </div>

        {/* --- 3D都市モデルの建物 --- */}
        <div style={pStyle}></div>
        <div className="mx-2 mb-3">
          <legend className="col-form-label bg-light mb-2" style={{ display: 'flex', alignItems: 'center', padding: '0.25rem 0.5rem' }}>
            3D都市モデルの建物
          </legend>
          <div className="form-check mt-2">
            <div className="d-flex">
              <div className="flex-fill">
                <input
                  className="form-check-input"
                  type="radio"
                  name="inlineRadioOptions"
                  id="radio_plateau_building_nodisp"
                  value="nodisp"
                  checked={radioSelect === "nodisp"}
                  onChange={(e) => {
                    setRadioSelect(e.target.value);
                    Func_chk_radio_plateau_building_nodisp(map);
                  }}
                />
                <label className="form-check-label" htmlFor="radio_plateau_building_nodisp">非表示</label>
              </div>
              <div className="flex-fill">
                <input
                  className="form-check-input"
                  type="radio"
                  name="inlineRadioOptions"
                  id="radio_plateau_building_2d"
                  value="2d"
                  checked={radioSelect === "2d"}
                  onChange={(e) => {
                    setRadioSelect(e.target.value);
                    Func_chk_radio_plateau_building_2d(map);
                  }}
                />
                <label className="form-check-label" htmlFor="radio_plateau_building_2d">2D表示</label>
              </div>
            </div>
            <div>
              <input
                className="form-check-input"
                type="radio"
                name="inlineRadioOptions"
                id="radio_plateau_building_3d"
                value="3d"
                checked={radioSelect === "3d"}
                onChange={(e) => {
                  setRadioSelect(e.target.value);
                  Func_chk_radio_plateau_building_3d(map);
                }}
              />
              <label className="form-check-label" htmlFor="radio_plateau_building_3d">3D表示</label>
            </div>
          </div>
        </div>

        {/* --- 3D都市モデルの地形 --- */}
        <div style={pStyle}></div>
        <div className="mx-2 mb-3">
          <legend className="col-form-label bg-light mb-2" style={{ display: 'flex', alignItems: 'center', padding: '0.25rem 0.5rem' }}>
            3D都市モデルの地形
          </legend>
          <div className="form-check mt-2">
            <input
              className="form-check-input"
              type="checkbox"
              id="ckb_plateau_dem"
              checked={isCheckDem}
              onChange={(e) => {
                setIsCheckDem(e.target.checked);
                Func_chk_plateau_dem(map);
              }}
            />
            <label className="form-check-label" htmlFor="ckb_plateau_dem">地形表示</label>
          </div>
        </div>

        {/* --- ご利用サポート --- */}
        <div style={pStyle}></div>
        <div className="mx-2 mb-3">
          {/* タイトルにアイコンを追加し、Bootstrapのスタイルを微調整 */}
          <legend className="col-form-label bg-light mb-2" style={{ display: 'flex', alignItems: 'center', padding: '0.25rem 0.5rem' }}>
            ご利用サポート
          </legend>

          <nav style={{ paddingLeft: '0.5rem', marginTop: '0.5rem' }}>
            <ul className="list-unstyled" style={{ fontSize: '0.85rem' }}>
              {[
                { href: MAPPLE_COM_LINKS.SUBSCRIPTION_NEWS, icon: 'fa-info-circle', text: 'サブスクリプションの詳細' },
                { href: MAPPLE_COM_LINKS.REGISTRY_NEWS, icon: 'fa-file-text-o', text: '不動産登記情報（土地）の購入' },
                { href: MAPPLE_COM_LINKS.FAQ_SITE, icon: 'fa-question-circle', text: 'よくあるご質問（FAQ）' },
                { onClick: openNiniNoticeDetail, icon: 'fa-info-circle', text: '任意座標の重ね表示（β版）について' },
                { href: MAPPLE_COM_LINKS.CONTACT_SITE, icon: 'fa-envelope', text: 'お問い合わせ' },
                { href: MAPPLE_COM_LINKS.PRODUCT_SITE, icon: 'fa-external-link', text: '製品サイト' },
                { href: MAPPLE_COM_LINKS.SURVEY_SITE, icon: 'fa-pencil-square-o', text: 'アンケート' },
              ].map((item, index) => (
                <li key={index} style={{ marginBottom: '0.8rem' }}> {/* 項目間の余白をここで確保 */}
                  {/* アイコンとテキストの並びは外部リンクとモーダルを開く項目で共通 */}
                  {(() => {
                    const linkStyle = {
                      display: 'flex',          // アイコンとテキストを分離
                      alignItems: 'flex-start', // 2行になってもアイコンを上に固定
                      lineHeight: '1.4',        // 項目内の行間をタイトに
                      textDecoration: 'none'
                    };
                    const linkInner = (
                      <>
                        <i
                          className={`fa ${item.icon} me-2`}
                          aria-hidden="true"
                          style={{ marginTop: '0.2rem', width: '1.2rem', textAlign: 'center' }} // アイコンの位置と幅を固定
                        ></i>
                        <span>{item.text}</span>
                      </>
                    );
                    // hrefを持たない項目はモーダルを開くボタンとして描画する
                    return item.href ? (
                      <a
                        href={item.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={linkStyle}
                      >
                        {linkInner}
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-link p-0 border-0 text-start"
                        style={{ ...linkStyle, fontSize: 'inherit' }}
                        onClick={item.onClick}
                      >
                        {linkInner}
                      </button>
                    );
                  })()}
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* プロモーション */}
        <PromotionalArea />
      </div>

      <MapSelectionOverlay
        map={map}
        isActive={isCheckImageDownload}
        onSelectionComplete={createImage}
        onDisable={() => setIsCheckImageDownload(false)}
      />
      {isCreatingImage && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
          }}
        >
          <Spinner animation="border" variant="dark" />
        </div>
      )}
    </div>
  );
};

export default LeftSide;